import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Mail, Lock, ShieldCheck, Truck, BadgeCheck, HeadphonesIcon } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const user = await login(email, password);
      toast.success(`Welcome back, ${user.name.split(" ")[0]}.`);
      nav(user.role === "admin" ? "/admin" : "/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  const google = () => {
    // DO NOT HARDCODE THE URL — this pattern is required by Emergent auth
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="container-book py-10 md:py-16 grid lg:grid-cols-2 gap-10 items-center min-h-[70vh]">
      {/* Left column — brand + trust */}
      <div className="hidden lg:block">
        <p className="text-primary font-semibold text-sm mb-2">Blessings Connection</p>
        <h1 className="font-heading font-bold text-4xl xl:text-5xl text-slate-900 leading-tight">
          Welcome back to the shelf.
        </h1>
        <p className="mt-4 text-lg text-slate-600 max-w-lg">
          Sign in to access your orders, saved books, tracking, and personalized textbook recommendations.
        </p>
        <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
          {[
            { icon: ShieldCheck, label: "Secure SSL checkout" },
            { icon: Truck,       label: "Free shipping over $35" },
            { icon: BadgeCheck,  label: "Authentic new books" },
            { icon: HeadphonesIcon, label: "USA support 7 days" },
          ].map((f) => (
            <div key={f.label} className="flex items-center gap-2.5 p-3 bg-white border border-slate-200 rounded-xl">
              <f.icon className="text-primary shrink-0" size={18} />
              <span className="text-sm text-slate-800 font-medium">{f.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Right column — form card */}
      <div className="w-full max-w-md mx-auto lg:mx-0 lg:ml-auto">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-lift p-6 md:p-8">
          <h2 className="font-heading font-bold text-2xl text-slate-900">Sign in</h2>
          <p className="text-sm text-slate-500 mt-1">Enter your email and password to continue.</p>

          <form onSubmit={submit} data-testid="login-form" className="mt-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-700 mb-1.5 block">Email address</label>
              <div className="relative">
                <Mail size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="pl-10 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <label className="text-xs font-semibold uppercase tracking-wider text-slate-700">Password</label>
                <Link to="/forgot-password" data-testid="login-forgot-link" className="text-xs font-medium text-primary hover:text-secondary">Forgot?</Link>
              </div>
              <div className="relative">
                <Lock size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="pl-10 h-12 rounded-xl border-slate-200 focus-visible:ring-2 focus-visible:ring-primary" />
              </div>
            </div>
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full h-12 rounded-xl bg-primary hover:bg-secondary text-white font-semibold text-base">
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="flex items-center gap-3 my-6">
            <span className="flex-1 h-px bg-slate-200" />
            <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Or</span>
            <span className="flex-1 h-px bg-slate-200" />
          </div>

          <Button
            data-testid="login-google"
            onClick={google}
            variant="outline"
            className="w-full h-12 rounded-xl border-slate-300 bg-white hover:bg-slate-50 text-slate-700 font-semibold"
          >
            Continue with Google
          </Button>

          <p className="mt-6 text-sm text-slate-600 text-center">
            New to Blessings Connection?{" "}
            <Link to="/register" className="font-semibold text-primary hover:text-secondary" data-testid="login-to-register">
              Create an account →
            </Link>
          </p>
        </div>

        <p className="mt-5 text-center text-xs text-slate-500">
          By signing in, you agree to our <Link to="/terms" className="underline hover:text-primary">Terms</Link> and <Link to="/privacy" className="underline hover:text-primary">Privacy Policy</Link>.
        </p>
      </div>
    </div>
  );
}
